import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import axios from 'axios';
import { url } from '../../../../env';
import { useQuoteAdjustmentDraft } from '../../../../Hooks/useQuoteAdjustmentDraft';
import { useCarCalculator } from '../../../../Hooks/useCarCalculator';
import { CarDataForm } from '../../../Cotizador/Steps/Step1/CarDataForm';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(value || 0);
};

export default function RecotizacionDialog({
    visible,
    onHide,
    persona,
    comparisonData,
    analysisData,
    invoiceFileId,
    toast,
    onDraftSaved,
    processId,
}) {
    const [, setStep] = useState(1);
    const [activeDraftBlock, setActiveDraftBlock] = useState(null);

    const { createDraft, updateDraft, markReady, getActiveDraftByBudget } = useQuoteAdjustmentDraft();

    // Build modified budgetData: inject invoice price if price changed
    const originalBudget = persona?.infoBudget;
    const modifiedBudgetData = React.useMemo(() => {
        if (!originalBudget || !comparisonData) return originalBudget;

        const modified = { ...originalBudget };

        // If price changed, override with invoice total and adjust down_payment proportionally
        if (comparisonData.price_changed && comparisonData.price?.xml) {
            const oldPrice = originalBudget.price || 1;
            const newPrice = comparisonData.price.xml;
            modified.price = newPrice;

            if (originalBudget.down_payment && oldPrice > 0) {
                const ratio = originalBudget.down_payment / oldPrice;
                modified.down_payment = Math.round(newPrice * ratio * 100) / 100;
            }
        }

        // If vehicle changed, clear vehicle fields to trigger fresh cascade
        // Use empty strings (not null) because useCarCalculator calls .toLowerCase() on these
        if (comparisonData.vehicle_changed) {
            modified.vehicle = {
                ...modified.vehicle,
                brand: '',
                model: '',
                description: '',
                typeAutoOrPickup: modified.vehicle?.typeAutoOrPickup || '',
                year: comparisonData.vehicle?.xml_suggestions?.year || modified.vehicle?.year,
            };
        }

        return modified;
    }, [originalBudget, comparisonData]);

    const internalVariables = useRef({
        secret: modifiedBudgetData?.secret || null,
        budget_id: modifiedBudgetData?.budget_id || null,
    });

    useEffect(() => {
        internalVariables.current.secret = modifiedBudgetData?.secret;
        internalVariables.current.budget_id = modifiedBudgetData?.budget_id;
    }, [modifiedBudgetData]);

    const {
        calculatorState,
        setState,
        loading,
        plazoVal,
        setPlazoVal,
        plazoTimer,
        buildSubmitPayload,
        errorsArray,
        loadInsurances,
        cleanErrors,
        setClass,
        sumAmountAndAccesories,
        typeRegime,
        residualValue,
        stateAmple,
        percentageDownPayment,
        hadleResidualValue,
        type_coverage,
        handleStatePlateChange,
    } = useCarCalculator(
        internalVariables,
        setStep,
        modifiedBudgetData,
        persona?.id,
        true, // isAdmin
        modifiedBudgetData?.secret,
        modifiedBudgetData?.budget_id
    );

    const fieldLabels = {
        name: 'Nombre', type: 'Tipo vehículo', type_coverage: 'Tipo cobertura',
        car_type_select: 'Tipo auto', condition: 'Condición', model_year: 'Año',
        brand: 'Marca', model_name: 'Modelo', model_version: 'Versión',
        zip: 'Código postal', colonia: 'Colonia', municipio: 'Municipio',
        ciudad: 'Ciudad', estado: 'Estado', amount: 'Precio',
        estado_placa: 'Estado placa', plazo: 'Plazo', enganche: 'Enganche',
        type_engine: 'Tipo motor', typeRegime: 'Régimen', residualValue: 'Valor residual',
        insurance: 'Seguro',
    };

    const handleSubmitAndSaveDraft = async () => {
        // 0. Validate all calculator fields before anything
        const built = buildSubmitPayload();
        if (!built) {
            const missing = (errorsArray || [])
                .map(key => fieldLabels[key] || key)
                .join(', ');
            toast.current?.show({
                severity: 'warn',
                summary: 'Campos incompletos',
                detail: missing ? `Faltan: ${missing}` : 'Verifica que todos los campos estén completos.',
                life: 6000,
            });
            return;
        }

        setActiveDraftBlock(null);
        try {
            // 1. Guard: check for active draft before doing anything
            const existingDraft = await getActiveDraftByBudget(originalBudget?.budget_id);
            if (existingDraft) {
                setActiveDraftBlock(existingDraft);
                return;
            }

            // 3. Call the correct cotizador endpoint (finance → cotizador, pure → cotizador2)
            // Always use the non-v2 endpoints here: down_payment in DB is already E_interno,
            // and the v2 wrappers would misinterpret it as Total Pago Inicial.
            const recotizEndpoint = built.endpoint.replace('-v2', '');
            const headers = {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };
            const cotizadorResponse = await axios.post(`${url}/${recotizEndpoint}`, built.payload, { headers });
            if (!cotizadorResponse.data?.resumen) {
                throw new Error('Error al cotizar: respuesta inválida del servidor');
            }
            const result = cotizadorResponse.data.data; // { infoBudget, id, name, ... }

            // 4. Create draft (handle 409 race condition)
            let draftResult;
            try {
                draftResult = await createDraft({
                    quoteProcessId: processId,
                    budgetId: originalBudget?.budget_id,
                    sourceInvoiceFileId: invoiceFileId,
                    comparisonType: comparisonData?.comparison_type,
                    invoiceSnapshot: comparisonData ? {
                        total: comparisonData.price?.xml,
                        vehicle_suggestions: comparisonData.vehicle?.xml_suggestions,
                        emisor: comparisonData.emisor,
                    } : null,
                });
            } catch (err) {
                if (err.response?.status === 409) {
                    setActiveDraftBlock(err.response.data?.data || { id: null, status: 'draft' });
                    return;
                }
                throw err;
            }

            if (!draftResult?.success) {
                throw new Error(draftResult?.message || 'Error al crear borrador');
            }

            const draftId = draftResult.data.id;

            // 5. Update draft with audit data (no quote_preview needed, budget already mutated)
            await updateDraft(draftId, {
                quote_input: {
                    price: calculatorState.rodac?.amount,
                    vehicle: {
                        brand: calculatorState.rodac?.brand,
                        model: calculatorState.rodac?.model_id,
                        year: calculatorState.rodac?.year,
                        type: calculatorState.rodac?.type,
                        description: calculatorState.rodac?.description_id,
                        status: calculatorState.rodac?.car_status_id,
                    },
                },
                diff: {
                    price_before: comparisonData?.price?.budget,
                    price_after: comparisonData?.price?.xml,
                    vehicle_changed: comparisonData?.vehicle_changed,
                    change_summary: comparisonData?.change_summary,
                },
            });

            // 6. Mark draft as ready for review
            await markReady(draftId);

            toast.current?.show({
                severity: 'success',
                summary: 'Recotización aplicada',
                detail: 'Los cambios se aplicaron al presupuesto.',
                life: 5000,
            });

            if (onDraftSaved) {
                onDraftSaved(draftId, result);  // result = datos de cotizador2
            }

            onHide();

        } catch (error) {
            console.error('Error en recotizacion:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || error.message || 'Error al procesar la recotizacion',
                life: 5000,
            });
        }
    };

    const priceChanged = comparisonData?.price_changed;
    const vehicleChanged = comparisonData?.vehicle_changed;

    return (
        <Dialog
            header={
                <div className="flex align-items-center gap-3">
                    <i className="pi pi-calculator text-xl"></i>
                    <span>Recotizacion desde factura</span>
                    {analysisData?.parsed_xml && (
                        <Tag
                            value={`Folio: ${analysisData.parsed_xml.serie || ''}${analysisData.parsed_xml.folio || ''}`}
                            severity="info"
                        />
                    )}
                </div>
            }
            visible={visible}
            style={{ width: '85vw', maxWidth: '1400px', minWidth: '700px', borderRadius: '14px' }}
            breakpoints={{ '960px': '95vw', '641px': '98vw' }}
            onHide={onHide}
            maximizable
            modal
            contentStyle={{ maxHeight: '85vh', overflowY: 'auto', overscrollBehavior: 'contain', borderRadius: '0 0 14px 14px' }}
            headerStyle={{ borderRadius: '14px 14px 0 0' }}
            draggable={false}
        >
            {/* Comparison Banner */}
            <div className="mb-4 p-3 border-round" style={{
                backgroundColor: vehicleChanged ? '#fef2f2' : '#fffbeb',
                border: `1px solid ${vehicleChanged ? '#fecaca' : '#fde68a'}`,
            }}>
                <div className="font-bold mb-2" style={{ color: vehicleChanged ? '#991b1b' : '#92400e' }}>
                    {vehicleChanged ? 'Cambio de vehiculo y/o precio detectado' : 'Diferencia de precio detectada'}
                </div>

                <div className="grid">
                    {priceChanged && (
                        <div className="col-12 md:col-6">
                            <div className="flex align-items-center gap-2">
                                <span className="text-sm text-gray-600">Precio anterior:</span>
                                <span className="font-medium">{formatCurrency(comparisonData.price.budget)}</span>
                                <i className="pi pi-arrow-right text-gray-400"></i>
                                <span className="text-sm text-gray-600">Precio factura:</span>
                                <span className="font-bold">{formatCurrency(comparisonData.price.xml)}</span>
                            </div>
                        </div>
                    )}

                    {vehicleChanged && comparisonData.vehicle?.mismatched_fields_detail && (
                        <div className="col-12 md:col-6">
                            {comparisonData.vehicle.mismatched_fields_detail.map((detail, idx) => (
                                <div key={idx} className="text-sm mb-1">
                                    <i className="pi pi-exclamation-circle mr-1" style={{ color: '#ef4444' }}></i>
                                    {detail}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {vehicleChanged && comparisonData.vehicle?.xml_suggestions && (
                    <div className="mt-2 p-2 bg-white border-round border-1 border-gray-200">
                        <small className="text-gray-500 block mb-1">Sugerencias de la factura:</small>
                        <div className="flex gap-3 flex-wrap">
                            {comparisonData.vehicle.xml_suggestions.year && (
                                <Tag value={`Año: ${comparisonData.vehicle.xml_suggestions.year}`} />
                            )}
                            {comparisonData.vehicle.xml_suggestions.brand && (
                                <Tag value={`Marca: ${comparisonData.vehicle.xml_suggestions.brand}`} />
                            )}
                            {comparisonData.vehicle.xml_suggestions.model && (
                                <Tag value={`Modelo: ${comparisonData.vehicle.xml_suggestions.model}`} />
                            )}
                            {comparisonData.vehicle.xml_suggestions.version_text && (
                                <Tag value={`Version: ${comparisonData.vehicle.xml_suggestions.version_text}`} />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Active draft block */}
            {activeDraftBlock && (
                <div className="mb-4 p-3 border-round" style={{
                    background: '#fffbeb',
                    border: '1px solid #f59e0b',
                }}>
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-lock" style={{ color: '#d97706' }}></i>
                        <span className="font-bold" style={{ color: '#92400e' }}>Borrador activo detectado</span>
                        <Tag
                            value={activeDraftBlock.status === 'ready_for_review' ? 'Lista para revisión' : 'Borrador'}
                            severity="warning"
                            style={{ fontSize: '0.7rem' }}
                        />
                    </div>
                    <p className="text-sm m-0 mb-3" style={{ color: '#78350f' }}>
                        Ya existe un borrador de ajuste para este presupuesto. Debe aprobarse o descartarse antes de crear uno nuevo.
                    </p>
                    <div className="flex justify-content-end">
                        <Button label="Cerrar" size="small" severity="secondary" outlined onClick={onHide} />
                    </div>
                </div>
            )}

            <Message
                severity="info"
                text="Los cambios se guardaran como borrador. El presupuesto actual del cliente no sera modificado hasta que envie la cotizacion ajustada."
                className="w-full mb-4"
            />

            {/* Calculator Form */}
            <CarDataForm
                state={calculatorState}
                setState={setState}
                loading={loading}
                plazoVal={plazoVal}
                setPlazoVal={setPlazoVal}
                plazoTimer={plazoTimer}
                submit={handleSubmitAndSaveDraft}
                loadInsurances={loadInsurances}
                cleanErrors={cleanErrors}
                setClass={setClass}
                sumAmountAndAccesories={sumAmountAndAccesories}
                typeRegime={typeRegime}
                residualValue={residualValue}
                stateAmple={stateAmple}
                percentageDownPayment={percentageDownPayment}
                hadleResidualValue={hadleResidualValue}
                type_coverage={type_coverage}
                handleStatePlateChange={handleStatePlateChange}
                isAdmin={true}
            />

        </Dialog>
    );
}
